import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { getFestival, updateFestival } from '../../services/api';
import { toast } from 'sonner';

const FESTIVALS = [
  { id: 'navratri', name: 'Navratri', emoji: '', colors: ['#FF6B35', '#FF4500', '#FFD700'], banner: 'Shubh Navratri! Jai Mata Di' },
  { id: 'diwali', name: 'Diwali', emoji: '', colors: ['#FFD700', '#FF8C00', '#FF4500'], banner: 'Happy Diwali! Festival of Lights' },
  { id: 'eid', name: 'Eid', emoji: '', colors: ['#2E8B57', '#006400', '#FFD700'], banner: 'Eid Mubarak!' },
  { id: 'holi', name: 'Holi', emoji: '', colors: ['#FF1493', '#00CED1', '#FFD700'], banner: 'Happy Holi! Festival of Colors' },
  { id: 'christmas', name: 'Christmas', emoji: '', colors: ['#C41E3A', '#006400', '#FFD700'], banner: 'Merry Christmas!' },
  { id: 'republic_day', name: 'Republic Day', emoji: '', colors: ['#FF9933', '#FFFFFF', '#138808'], banner: 'Happy Republic Day! Jai Hind' },
  { id: 'independence_day', name: 'Independence Day', emoji: '', colors: ['#FF9933', '#FFFFFF', '#138808'], banner: 'Happy Independence Day!' },
  { id: 'ganesh_chaturthi', name: 'Ganesh Chaturthi', emoji: '', colors: ['#FF6B35', '#FFD700', '#FF4500'], banner: 'Ganpati Bappa Morya!' },
  { id: 'makar_sankranti', name: 'Makar Sankranti', emoji: '', colors: ['#FF8C00', '#FFD700', '#4169E1'], banner: 'Happy Makar Sankranti!' },
  { id: 'raksha_bandhan', name: 'Raksha Bandhan', emoji: '', colors: ['#FF69B4', '#FFD700', '#9370DB'], banner: 'Happy Raksha Bandhan!' },
  { id: 'custom', name: 'Custom', emoji: '', colors: ['#2E3A6F', '#4a5a9f', '#FFD700'], banner: 'Custom Festival' },
];

const FestivalAdmin = () => {
  const [setting, setSetting] = useState({ active: false, festival_id: '', banner_text: '', custom_name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getFestival();
        if (res.data) setSetting({ active: false, festival_id: '', banner_text: '', custom_name: '', ...res.data });
      } catch {}
    };
    fetch();
  }, []);

  const selectedFestival = FESTIVALS.find(f => f.id === setting.festival_id);

  const handleSelectFestival = (festival) => {
    setSetting({
      ...setting,
      festival_id: festival.id,
      banner_text: festival.banner,
      colors: festival.colors
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFestival(setting);
      toast.success(setting.active ? 'Festival theme activated!' : 'Festival theme deactivated');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="festival-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Festival Effects</h1>
          <p className="text-sm text-gray-500">Select a festival to activate themed decorations on the website</p>
        </div>
        <Button onClick={handleSave} className="bg-[#2E3A6F]" disabled={saving} data-testid="save-festival-btn">
          {saving ? 'Saving...' : 'Save & Apply'}
        </Button>
      </div>

      {/* Active Toggle */}
      <Card>
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Enable Festival Theme</h3>
            <p className="text-sm text-gray-500">When active, a festive banner and decorations will show on the website</p>
          </div>
          <Switch checked={setting.active} onCheckedChange={(v) => setSetting({ ...setting, active: v })} data-testid="festival-active-toggle" />
        </CardContent>
      </Card>

      {/* Festival Selection */}
      <div>
        <Label className="text-sm font-semibold mb-3 block">Select Festival</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {FESTIVALS.map(festival => (
            <button
              key={festival.id}
              onClick={() => handleSelectFestival(festival)}
              className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                setting.festival_id === festival.id
                  ? 'border-[#2E3A6F] bg-[#2E3A6F]/5 shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`festival-${festival.id}`}
            >
              <div className="text-2xl mb-1">{festival.emoji}</div>
              <p className="text-sm font-semibold text-gray-900">{festival.name}</p>
              <div className="flex gap-1 mt-2">
                {festival.colors.map((c, i) => (
                  <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: c }} />
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Banner Text */}
      {setting.festival_id && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="font-semibold text-gray-900">Banner Settings</h3>
            <div>
              <Label>Banner Text</Label>
              <Input
                value={setting.banner_text}
                onChange={(e) => setSetting({ ...setting, banner_text: e.target.value })}
                placeholder="Festival greeting message..."
                data-testid="festival-banner-text"
              />
            </div>
            {setting.festival_id === 'custom' && (
              <div>
                <Label>Custom Festival Name</Label>
                <Input
                  value={setting.custom_name || ''}
                  onChange={(e) => setSetting({ ...setting, custom_name: e.target.value })}
                  placeholder="e.g. Company Anniversary"
                />
              </div>
            )}

            {/* Preview */}
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Preview</Label>
              <div
                className="rounded-lg py-3 px-4 text-center text-white font-medium text-sm overflow-hidden relative"
                style={{
                  background: selectedFestival ? `linear-gradient(135deg, ${selectedFestival.colors[0]}, ${selectedFestival.colors[1]}, ${selectedFestival.colors[2]})` : '#2E3A6F'
                }}
              >
                <span className="relative z-10">{selectedFestival?.emoji} {setting.banner_text} {selectedFestival?.emoji}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FestivalAdmin;
